/* eslint-disable */
import { Injectable } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { MSLatLng } from '../models/map.model';
import { identifyPlatform, PlatformEnum } from '../utils/platform.utils';
import { CONSTANTS } from './config.service';
import { LoggerService } from './logger.service';
import { MapsBingService } from './maps-bing.service';
import { MapsNativeService } from './maps-native/maps-native.service';
import { PrefsService } from './prefs.service';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';

@Injectable({
  providedIn: 'root'
})
export class MapsService {
  usingGoogleMaps: boolean;
  usingBingMaps: boolean;
  mapsProvider: MapsNativeService | MapsBingService;
  markers: any;
  infoWindows: any;

  constructor(
    private platform: Platform,
    private alertCtrl: AlertController,
    private prefs: PrefsService,
    private translate: TranslateService,
    private logger: LoggerService,
    private bingMaps: MapsBingService,
    private mapsNative: MapsNativeService
  ) {
    this.logger.log('Maps constructor', MSRemoteLoggerCategory.MAPS_SERVICE);
    this.setMapsProvider();
  }

  shiftMarkersStartStop(a) {
    this.bingMaps.shiftMarkersStartStop(a);
  }

  /************************************************************************************************************************************************************************
     * Set the correct provider - either native or js
    /***********************************************************************************************************************************************************************/

  // set maps provider in memory if it's changed
  setMapsProvider() {
    this.mapsProvider = CONSTANTS.IS_DEVICE ? this.mapsNative : this.bingMaps;
    this.usingGoogleMaps = CONSTANTS.IS_DEVICE === true;
    this.usingBingMaps = CONSTANTS.IS_DEVICE === false;
  }

  /************************************************************************************************************************************************************************
     * Initialize map
    /***********************************************************************************************************************************************************************/

  // init the map on each page
  init(a, b, c) {
    return this.mapsProvider.init(a, b, c);
  }

  // init the map on each page
  //  initTransit(a, b, c) {
  //     if (!this.mapsProvider) { this.setMapsProvider() };
  //     if (this.usingGoogleMaps) {
  //         return this.mapsNative.initTransit(a, b, c);
  //     } else {
  //         return this.bingMaps.init(a, b, c);
  //     }

  // }

  // if using native maps, load js places service for pages with address search
  loadPlacesService(a, b, c) {
    if (this.usingGoogleMaps === true) {
      return this.bingMaps.loadPlaces(a, b, c);
    }
    return Promise.resolve(true);
  }

  /************************************************************************************************************************************************************************
     * Update map view
    /***********************************************************************************************************************************************************************/

  // segment bars allow route-level view, or zoomed-in view
  showOverview(a?, b?, points?: MSLatLng[]): Promise<void> {
    return this.mapsProvider.showOverview(a, b, points);
  }
  showPoint(a, b?, c?) {
    this.mapsProvider.showPoint(a, b, c);
  }

  /************************************************************************************************************************************************************************
     * Add map objects
    /***********************************************************************************************************************************************************************/

  // addMarkersAndPathTransit(a) {
  //     console.log(`Transit START HERE addMarkersAndPathTransit() begins`)
  //     if (this.usingBingMaps) {
  //         this.bingMaps.addMarkersAndPathTransit(a)
  //         this.updateMapItems();
  //         return Promise.resolve();
  //     } else {
  //         return this.mapsProvider.addMarkersAndPathTransitAsync(a)
  //             .then(() => {
  //                 this.updateMapItems();
  //                 return Promise.resolve();
  //             })
  //     }
  // }

  // used on add-times, mode-change, verify, map-route, survey-map. on these pages, we have a known route to display
  addMarkersAndPath(a, b) {
    if (this.usingBingMaps) {
      this.bingMaps.addMarkersAndPath(a, b);
      this.updateMapItems();
      return Promise.resolve();
    } else {
      return this.mapsNative.addMarkersAndPathAsync(a, b).then(() => {
        this.updateMapItems();
        return Promise.resolve();
      });
    }
  }

  // used on map-region and survey-map for long-distance. here we have a known pair of points to display. instead of showing a marker, we show an image with text for START/STOP.
  addCirclesAndMarkers(a, b) {
    if (this.usingBingMaps) {
      this.bingMaps.addCirclesAndMarkers(a, b);
      this.updateMapItems();
      return Promise.resolve();
    } else {
      return this.mapsNative.addCirclesAndMarkersAsync(a, b).then(() => {
        this.updateMapItems();
        return Promise.resolve();
      });
    }
  }

  // used on add-start, add-stop, add-another, add-split-stop, merge, split
  addMarker(marker) {
    if (this.usingBingMaps) {
      this.bingMaps.addMarker(marker);
      this.updateMapItems();
      return Promise.resolve();
    } else {
      return this.mapsNative.addMarkerAsync(marker).then(() => {
        this.updateMapItems();
      });
    }
  }

  // used on verify
  addPath(a, b, c?, d?) {
    if (this.usingBingMaps) {
      this.bingMaps.addPath(a, b, c, d);
      this.updateMapItems();
      return Promise.resolve();
    } else {
      return this.mapsNative.addPathAsync(a, b, c, d).then(() => {
        this.updateMapItems();
      });
    }
  }

  //used on add-start, add-stop, add-another, merge, split, add-split-stop
  addInfoWindow(a) {
    // replaced infowindows for native with marker titles and snippets. they just don't work well on native
    if (this.usingBingMaps) {
      this.bingMaps.addInfoWindow(a);
    } else {
      this.mapsNative.addInfoWindow(a);
    }
    this.updateMapItems();
  }

  async addInfoWindowAsync(opts: { markerIdx: number; content: { title: string; snippet: string } }) {
    await this.mapsNative.addInfoWindow(opts);
    this.updateMapItems();
  }

  /************************************************************************************************************************************************************************
     * Remove map objects
    /***********************************************************************************************************************************************************************/

  removeMarker(idx: number) {
    if (this.usingBingMaps) {
      this.bingMaps.removeMarker(idx);
      this.updateMapItems();
      return Promise.resolve();
    } else {
      return this.mapsNative.removeMarkerAsync(idx).then(() => {
        this.updateMapItems();
      });
    }
  }

  clear() {
    if (this.usingBingMaps) {
      this.bingMaps.clear();
      this.updateMapItems();
      return Promise.resolve();
    } else {
      return this.mapsNative.clearAsync().then(() => {
        this.updateMapItems();
      });
    }
  }

  async removeMap(isModal?) {
    if (this.usingBingMaps) {
      // don't think we need this for js
      return Promise.resolve();
    } else {
      await this.mapsNative.removeMap();
      this.updateMapItems();
    }
  }

  closeInfoWindow(a) {
    if (this.usingBingMaps) {
      this.bingMaps.closeInfoWindow(a);
    } else {
      //a.close();
    }
  }

  /************************************************************************************************************************************************************************
     * Remove map objects
    /***********************************************************************************************************************************************************************/

  getMapCenter() {
    return this.mapsProvider.getMapCenter();
  } // if placing a marker - add-start, add-stop, add-split-stop
  getZoom() {
    if (this.usingBingMaps) {
      return this.bingMaps.map.getZoom();
    } else {
      return this.mapsNative.getMapZoom();
    }
  } // if placing a marker - add-start, add-stop

  // js only
  selectPlace(a) {
    return this.bingMaps.selectPlace(a);
  }
  searchPlace(a) {
    return this.bingMaps.searchPlace(a);
  }
  resizeMap() {
    if (this.usingBingMaps) {
      // this.mapsJs.resizeMap();
    }
  }

  reverseGeocode(a: MSLatLng) {
    return this.bingMaps.reverseGeocode(a);
  }

  /************************************************************************************************************************************************************************
   * SETTERS
   ***********************************************************************************************************************************************************************/

  setMapCenter(a) {
    return this.mapsProvider.setMapCenter(a);
  } // after place search - add-start, add-stop, add-split-stop
  setMapZoom(a) {
    return this.mapsProvider.setMapZoom(a);
  } // after place search - add-start, add-stop
  setMapBounds(a) {
    return this.mapsProvider.setMapBounds(a);
  } //

  setPoints(a) {
    this.mapsProvider.points = a;
  } // if we updated the trip points without re-initing the map, need to update.

  // native only
  setMapType(a) {
    if (!this.usingBingMaps) {
      this.mapsNative.setMapType(a);
    } else {
      if (a == 'MAP_TYPE_HYBRID') {
        this.bingMaps.setMapTypeId('aerial');
      } else {
        this.bingMaps.setMapTypeId('road');
      }
    }
  }

  setMarkerVisible(marker, value) {
    if (this.usingBingMaps) {
      this.bingMaps.setVisible(marker, value);
    } else {
      marker.setVisible(value);
    }
  }
  async setMarkerVisibleAsync(marker, value): Promise<void> {
    if (this.usingBingMaps) {
      this.bingMaps.setVisible(marker, value);
      return Promise.resolve();
    } else {
      return marker.setVisible(value);
    }
  }

  setMarkerPosition(marker, position) {
    if (this.usingBingMaps) {
      this.bingMaps.setMarkerPosition(marker, position);
    } else {
      marker.setPosition(position);
    }
  }

  async setMarkerPositionAsync(marker, position): Promise<void> {
    if (this.usingBingMaps) {
      this.bingMaps.setMarkerPosition(marker, position);
      return Promise.resolve();
    } else {
      return marker.setPosition(position);
    }
  }

  setMarkerIcon(marker, options) {
    if (this.usingBingMaps) {
      this.bingMaps.setMarkerIcon(marker, options);
    } else {
      const opts = { url: options.url, anchor: [15, 45] };
      if (options.hasOwnProperty('anchor')) {
        opts.anchor = options.anchor;
      }
      marker.setIcon(opts);
    }
  }

  addViewChangeEndEvent() {
    this.mapsProvider.addViewChangeEndEvent();
  }

  updateMapItems() {
    this.markers = this.mapsProvider.markers;
    this.infoWindows = this.mapsProvider.infoWindows;
  }

  async presentAlertGeocoderInstructions() {
    let mapSearchTranslation: string;
    if (identifyPlatform(this.platform) == PlatformEnum.BROWSER) {
      mapSearchTranslation = this.translate.instant('MAP_SEARCH_HELP_MESSAGE_MAP_SEARCH_BMOVE');
    } else {
      mapSearchTranslation = this.translate.instant('MAP_SEARCH_HELP_MESSAGE_MAP_SEARCH');
    }

    const alert = await this.alertCtrl.create({
      header: this.translate.instant('MAP_SEARCH_HELP_HEADER'),
      // message: this.translate.instant('MAP_SEARCH_HELP_MESSAGE'),
      message:
        this.translate.instant('MAP_SEARCH_HELP_MESSAGE_INTRO') +
        '<ul><li>' +
        this.translate.instant('MAP_SEARCH_HELP_MESSAGE_ADDRESS_SEARCH') +
        '</li><li>' +
        mapSearchTranslation +
        '</li></ul>',
      cssClass: 'alert-map-help',
      buttons: [
        {
          text: this.translate.instant('BUTTON_OK')
        }
      ]
    });
    await alert.present();

    alert.onDidDismiss().then((data) => {
      this.prefs.getStorePromise('didGeocodeHelp', true);
    });
  }

  async showGeocoderInstructions() {
    this.prefs.getPromiseOrDefault('didGeocodeHelp').then((res) => {
      if (res == true) {
        return Promise.resolve();
      }
      this.presentAlertGeocoderInstructions();
    });
  }
}
